/**
 * ScriptHeadingMapper - Builds bidirectional mapping between scripts and headings
 *
 * Parses woven markdown to associate tangle markers with their parent headings.
 * This enables syncing script completion status with heading checklists.
 */

/**
 * Bidirectional mapping between scripts and headings
 */
export interface ScriptHeadingMapping {
  /** Map from script name to array of heading slugs that contain blocks for this script */
  scriptToHeadings: Map<string, string[]>;
  /** Map from heading slug to array of script names contained under that heading */
  headingToScripts: Map<string, string[]>;
}

/**
 * Slugify text to create a URL-friendly identifier.
 * Must match the slugify logic in ChecklistEnricher.ts for consistency.
 */
function slugify(text: string): string {
  return text
    .toLowerCase()
    .replace(/[^\w\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '');
}

/**
 * Regex patterns for parsing
 */
const HEADING_REGEX = /^(#{1,6})\s+(.+)$/;
const TANGLE_CUSTOM_REGEX = /<!--\s*tangle:\s*([^>]+?)\s*-->/;
const TANGLE_QUARTO_REGEX = /^```.*tangle="([^"]+)"/;

/**
 * Build bidirectional mapping between scripts and headings
 *
 * Scans woven markdown to find:
 * - Headings (# to ######)
 * - Tangle markers (<!-- tangle: script.sh --> or ```{bash, tangle="script.sh"})
 *
 * Associates each tangle marker with the most recent heading above it.
 *
 * @param wovenMarkdown - The woven markdown content (after embeds expanded, optionally after var substitution)
 * @param resolvedScriptNames - Optional array of resolved script names (from scriptMeta) to filter/match against
 * @returns Bidirectional mapping between scripts and headings
 */
export function buildScriptHeadingMapping(
  wovenMarkdown: string,
  resolvedScriptNames?: string[]
): ScriptHeadingMapping {
  const lines = wovenMarkdown.split('\n');
  const scriptToHeadings = new Map<string, string[]>();
  const headingToScripts = new Map<string, string[]>();

  // Track current heading and slug deduplication
  let currentHeading: { text: string; slug: string } | null = null;
  const usedSlugs = new Map<string, number>();

  // Set of resolved names for quick lookup (if provided)
  const resolvedSet = resolvedScriptNames ? new Set(resolvedScriptNames) : null;

  for (const line of lines) {
    // Check for heading
    const headingMatch = line.match(HEADING_REGEX);
    if (headingMatch) {
      const headingText = headingMatch[2].trim();
      const baseSlug = slugify(headingText);

      // Handle duplicate slugs (same logic as ChecklistEnricher.getUniqueSlug)
      const count = usedSlugs.get(baseSlug) ?? 0;
      usedSlugs.set(baseSlug, count + 1);
      const slug = count === 0 ? baseSlug : `${baseSlug}-${count + 1}`;

      currentHeading = { text: headingText, slug };
      continue;
    }

    // Check for tangle marker (custom format)
    const customMatch = line.match(TANGLE_CUSTOM_REGEX);
    if (customMatch && currentHeading) {
      const scriptNames = customMatch[1]
        .split(',')
        .map(part => part.trim())
        .filter(Boolean);
      for (const scriptName of scriptNames) {
        addMapping(scriptName, currentHeading.slug, scriptToHeadings, headingToScripts, resolvedSet);
      }
      continue;
    }

    // Check for tangle marker (Quarto format)
    const quartoMatch = line.match(TANGLE_QUARTO_REGEX);
    if (quartoMatch && currentHeading) {
      const scriptName = quartoMatch[1].trim();
      addMapping(scriptName, currentHeading.slug, scriptToHeadings, headingToScripts, resolvedSet);
      continue;
    }
  }

  return { scriptToHeadings, headingToScripts };
}

/**
 * Add a script-heading association to both maps
 */
function addMapping(
  scriptName: string,
  headingSlug: string,
  scriptToHeadings: Map<string, string[]>,
  headingToScripts: Map<string, string[]>,
  resolvedSet: Set<string> | null
): void {
  // If we have resolved names, skip scripts that don't match
  if (resolvedSet && !resolvedSet.has(scriptName)) {
    return;
  }

  // Add to scriptToHeadings
  if (!scriptToHeadings.has(scriptName)) {
    scriptToHeadings.set(scriptName, []);
  }
  const headings = scriptToHeadings.get(scriptName)!;
  if (!headings.includes(headingSlug)) {
    headings.push(headingSlug);
  }

  // Add to headingToScripts
  if (!headingToScripts.has(headingSlug)) {
    headingToScripts.set(headingSlug, []);
  }
  const scripts = headingToScripts.get(headingSlug)!;
  if (!scripts.includes(scriptName)) {
    scripts.push(scriptName);
  }
}
